import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  productService,
  Product,
  ProductVariant,
} from "../../services/productService";
import { orderService, CreateOrderRequest } from "../../services/orderService";
import ProductImageGallery from "../../components/product/ProductImageGallery";
import VariantSelector from "../../components/product/VariantSelector";
import ProductSpecifications from "../../components/product/ProductSpecifications";
import OrderForm, { OrderFormRef } from "../../components/order/OrderForm";
import Footer from "../../components/layout/Footer";
import { ProductDetailSkeleton } from "../../components/product/ProductDetailSkeleton";

const ProductDetail: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVariants, setSelectedVariants] = useState<
    Record<string, string>
  >({}); // variantType -> variantId
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const orderFormRef = useRef<OrderFormRef>(null);

  // Check form validity periodically
  useEffect(() => {
    const interval = setInterval(() => {
      if (orderFormRef.current) {
        setIsFormValid(orderFormRef.current.isValid());
      }
    }, 300);
    return () => clearInterval(interval);
  }, [product]);

  useEffect(() => {
    if (productId) {
      loadProduct();
    }
  }, [productId]);

  // Track ViewContent event when product is loaded
  useEffect(() => {
    if (product && window.fbq) {
      window.fbq("track", "ViewContent", {
        content_name: product.name,
        content_ids: [product.id],
        content_type: "product",
        value: Number(product.basePrice),
        currency: "DZD",
      });
    }
  }, [product]);

  const loadProduct = async () => {
    if (!productId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await productService.getById(productId);
      setProduct(data);

      // Set default selections: first available variant of each type
      if (data.variants && data.variants.length > 0) {
        const variantsByType: Record<string, ProductVariant[]> = {};
        data.variants.forEach((variant) => {
          if (variant.isAvailable) {
            if (!variantsByType[variant.variantType]) {
              variantsByType[variant.variantType] = [];
            }
            variantsByType[variant.variantType].push(variant);
          }
        });

        const defaults: Record<string, string> = {};
        Object.entries(variantsByType).forEach(([type, typeVariants]) => {
          // Sort by displayOrder and select the first one
          const sorted = [...typeVariants].sort(
            (a, b) => a.displayOrder - b.displayOrder
          );
          if (sorted.length > 0) {
            defaults[type] = sorted[0].id;
          }
        });
        setSelectedVariants(defaults);
      }
    } catch (err: any) {
      setError(err.message || "فشل تحميل المنتج");
    } finally {
      setLoading(false);
    }
  };

  const handleOrderSubmit = async (orderData: CreateOrderRequest) => {
    try {
      const order = await orderService.create(orderData);
      setOrderNumber(order.orderNumber);

      // Track Purchase event on frontend with same event_id for deduplication
      // (Conversion API is also called from backend with the same event_id)
      if (window.fbq && product && order.pixelEventId) {
        window.fbq("track", "Purchase", {
          value: Number(order.finalPrice || product.basePrice || 0),
          currency: "DZD",
          content_name: product.name,
          content_ids: [product.id],
          content_type: "product",
          order_id: order.orderNumber,
        }, {
          eventID: order.pixelEventId // Use same event ID for deduplication
        });
      }

      // Success will be handled by onSuccess callback
    } catch (error: any) {
      // Re-throw error so OrderForm can handle it
      throw error;
    }
  };

  const handleOrderSuccess = () => {
    setOrderSuccess(true);
  };

  if (loading) {
    return <ProductDetailSkeleton />;
  }

  if (error || !product) {
    return (
      <div className="container mx-auto px-4 py-8 bg-background-light dark:bg-background-dark min-h-screen">
        <div className="text-red-600 dark:text-red-400 text-center">
          {error || "المنتج غير موجود"}
        </div>
        <button
          onClick={() => navigate("/catalog")}
          className="mt-4 mx-auto block text-[#13a4ec] hover:underline"
        >
          العودة إلى كتالوج المنتجات
        </button>
      </div>
    );
  }

  // Calculate price based on selected variants
  // priceAdjustment is added to basePrice (can be positive or negative)
  const basePriceNum = Number(product.basePrice);

  // Calculate total price adjustment from all selected variants
  let totalPriceAdjustment = 0;
  Object.values(selectedVariants).forEach((variantId) => {
    const variant = product.variants.find((v) => v.id === variantId);
    if (variant) {
      const adjustment = Number(variant.priceAdjustment) || 0;
      if (!isNaN(adjustment)) {
        totalPriceAdjustment += adjustment;
      }
    }
  });

  // Calculate final price: basePrice + sum of all variant adjustments
  const priceBeforeDiscount = basePriceNum + totalPriceAdjustment;

  // Get the primary selected variant (first one) for order submission
  const primaryVariantId =
    Object.values(selectedVariants).length > 0
      ? Object.values(selectedVariants)[0]
      : null;

  // Discount logic: basePrice is already the final (discounted) price
  // originalPrice is the original price before discount
  // Only show discount if originalPrice exists and is higher than basePrice
  const originalPriceNum = product.originalPrice
    ? Number(product.originalPrice)
    : null;
  
  // Check if there's a valid discount (originalPrice > basePrice)
  const hasDiscount = originalPriceNum !== null && originalPriceNum > basePriceNum;
  const discountPercentage = hasDiscount && product.discountPercentage
    ? Number(product.discountPercentage)
    : 0;
  
  // Final price is the basePrice (already discounted) + variant adjustments
  // Don't apply discount again - basePrice is already the discounted price
  const finalPrice = priceBeforeDiscount;

  // For display: show originalPrice if discount exists, otherwise show finalPrice
  const originalPrice = hasDiscount && originalPriceNum !== null
    ? originalPriceNum + totalPriceAdjustment // Add variant adjustments to original price too
    : priceBeforeDiscount;

  const handleVariantChange = (variantType: string, variantId: string) => {
    setSelectedVariants((prev) => ({
      ...prev,
      [variantType]: variantId,
    }));
  };

  return (
    <div className="product-detail-page bg-background-light dark:bg-background-dark min-h-screen">
      {/* Mobile Top App Bar */}
      <div className="md:hidden flex items-center bg-background-light dark:bg-background-dark p-4 pb-2 justify-between sticky top-0 z-10 border-b border-slate-200 dark:border-slate-800">
        <h2 className="text-[#0d171b] dark:text-white text-lg font-bold leading-tight tracking-[-0.015em] flex-1 text-center">
          تفاصيل المنتج
        </h2>
        <button
          onClick={() => navigate("/catalog")}
          className="text-[#0d171b] dark:text-white flex size-10 shrink-0 items-center justify-center"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>

      {/* Desktop Header */}
      <div className="hidden md:block bg-background-light dark:bg-background-dark border-b border-slate-200 dark:border-slate-800">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/catalog")}
            className="text-[#0d171b] dark:text-white flex items-center gap-2 hover:text-primary transition"
          >
            <span className="material-symbols-outlined">arrow_forward</span>
            <span>العودة إلى الكاتالوج</span>
          </button>
          <h1 className="text-2xl font-bold text-[#0d171b] dark:text-white">
            تفاصيل المنتج
          </h1>
          <div className="w-24"></div> {/* Spacer for centering */}
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 py-4 md:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {/* Image Gallery - Desktop: Left, Mobile: Full width */}
          <div className="space-y-4">
            {/* Desktop Main Image with Thumbnails */}
            <div className="hidden md:block space-y-4">
              {/* Main Image */}
              <div className="w-full aspect-square bg-slate-100 dark:bg-slate-800 rounded-xl overflow-hidden">
                {product.images && product.images.length > 0 ? (
                  <img
                    src={product.images[selectedImageIndex].imageUrl}
                    alt={
                      product.images[selectedImageIndex].altText || product.name
                    }
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-slate-400">لا توجد صورة</span>
                  </div>
                )}
              </div>

              {/* Thumbnail Gallery */}
              {product.images && product.images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {product.images.map((image, index) => (
                    <button
                      key={image.id}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden border-2 transition ${
                        selectedImageIndex === index
                          ? "border-primary ring-2 ring-primary/30"
                          : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                      }`}
                    >
                      <img
                        src={image.imageUrl}
                        alt={image.altText || `${product.name} - ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Image Carousel */}
            <div className="md:hidden">
              <div className="flex overflow-x-auto [-ms-scrollbar-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden snap-x snap-mandatory -mx-4 px-4">
                <div className="flex items-stretch gap-3">
                  {product.images && product.images.length > 0 ? (
                    product.images.map((image, index) => (
                      <div
                        key={image.id}
                        className="flex-shrink-0 w-full snap-center"
                      >
                        <img
                          src={image.imageUrl}
                          alt={
                            image.altText || `${product.name} - ${index + 1}`
                          }
                          className="w-full aspect-square object-cover rounded-xl"
                        />
                      </div>
                    ))
                  ) : (
                    <div className="w-full bg-slate-200 dark:bg-slate-700 aspect-square rounded-xl flex items-center justify-center">
                      <span className="text-slate-400">لا توجد صورة</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Mobile Page Indicators */}
              {product.images && product.images.length > 1 && (
                <div className="flex w-full flex-row items-center justify-center gap-3 py-2">
                  {product.images.map((_, index) => (
                    <div
                      key={index}
                      className={`h-2 rounded-full transition ${
                        selectedImageIndex === index
                          ? "bg-primary w-6"
                          : "bg-slate-300 dark:bg-slate-600 w-2"
                      }`}
                    ></div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Product Info - Desktop: Right, Mobile: Below images */}
          <div className="space-y-6">
            {/* Product Info Section */}
            <div>
              <h1 className="text-[28px] md:text-3xl lg:text-4xl font-bold text-[#0d171b] dark:text-white tracking-tight leading-tight mb-3">
                {product.name}
              </h1>

              {/* Rating */}
              {product.rating && product.rating > 0 && (
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <span
                        key={i}
                        className={`text-lg ${
                          i < Math.round(Number(product.rating || 0))
                            ? "text-yellow-400"
                            : "text-slate-300 dark:text-slate-600"
                        }`}
                      >
                        ★
                      </span>
                    ))}
                  </div>
                  <span className="text-slate-600 dark:text-slate-400 text-sm">
                    {Number(product.rating).toFixed(1)} (
                    {product.ratingCount || 0} تقييم)
                  </span>
                </div>
              )}

              {/* Price Section */}
              <div className="mb-6">
                {hasDiscount ? (
                  <div className="flex items-center gap-3 flex-wrap">
                    <h2 className="text-primary text-[22px] md:text-3xl font-bold leading-tight">
                      {Number(finalPrice).toFixed(2)} د.ج
                    </h2>
                    <span className="text-slate-400 dark:text-slate-500 line-through text-lg md:text-xl">
                      {Number(originalPrice).toFixed(2)} د.ج
                    </span>
                    <span className="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-2 py-1 rounded text-sm font-semibold">
                      خصم {discountPercentage.toFixed(0)}%
                    </span>
                  </div>
                ) : (
                  <h2 className="text-primary text-[22px] md:text-3xl font-bold leading-tight">
                    {Number(finalPrice).toFixed(2)} د.ج
                  </h2>
                )}
              </div>

              {/* Stock Status */}
              <div className="mb-6">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold ${
                      product.inStock ?? true
                        ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                        : "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400"
                    }`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      {product.inStock ?? true ? (
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      ) : (
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      )}
                    </svg>
                    {product.inStock ?? true ? "متوفر في المخزون" : "نفذ من المخزون"}
                  </span>
                  {!(product.inStock ?? true) && (
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      (يمكن الطلب)
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Variants Section */}
            {product.variants && product.variants.length > 0 && (
              <div className="space-y-4 pb-6 border-b border-slate-200 dark:border-slate-800">
                <VariantSelector
                  variants={product.variants}
                  selectedVariants={selectedVariants}
                  onVariantChange={handleVariantChange}
                  basePrice={product.basePrice}
                />
              </div>
            )}

            {/* Description Section */}
            {product.description && (
              <div className="py-6 border-b border-slate-200 dark:border-slate-800">
                <h3 className="text-[#0d171b] dark:text-white text-lg md:text-xl font-bold leading-tight mb-3">
                  الوصف
                </h3>
                <p className="text-slate-600 dark:text-slate-400 text-base md:text-lg leading-relaxed">
                  {product.description}
                </p>
              </div>
            )}

            {/* Specifications Section */}
            {product.specifications && product.specifications.length > 0 && (
              <div className="py-6 border-b border-slate-200 dark:border-slate-800">
                <ProductSpecifications
                  specifications={product.specifications}
                />
              </div>
            )}

            {/* Shipping Info Form */}
            {!orderSuccess && (
              <div className="pt-6">
                <h3 className="text-[#0d171b] dark:text-white text-lg md:text-xl font-bold leading-tight mb-4">
                  معلومات التوصيل
                </h3>
                <OrderForm
                  ref={orderFormRef}
                  productId={product.id}
                  variantId={primaryVariantId}
                  onSubmit={handleOrderSubmit}
                  onSuccess={handleOrderSuccess}
                  onLoadingChange={setIsSubmittingOrder}
                />
              </div>
            )}

            {/* Success Message */}
            {orderSuccess && (
              <div className="pt-6">
                <div className="p-6 bg-green-50 dark:bg-green-900/30 border-2 border-green-400 dark:border-green-600 rounded-xl shadow-lg">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                        <span className="material-symbols-outlined text-white text-2xl">
                          check_circle
                        </span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-xl mb-2 text-green-800 dark:text-green-200">
                        تم تأكيد الطلب بنجاح!
                      </h3>
                      <p className="mb-3 text-green-700 dark:text-green-300">
                        شكراً لك! تم إنشاء طلبك بنجاح وسيتم معالجته قريباً.
                      </p>
                      {orderNumber && (
                        <div className="mb-3 p-3 bg-white dark:bg-slate-800 rounded-lg border border-green-200 dark:border-green-700">
                          <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                            رقم الطلب:
                          </p>
                          <p className="text-lg font-bold text-green-700 dark:text-green-300">
                            {orderNumber}
                          </p>
                        </div>
                      )}
                      <p className="text-sm text-green-600 dark:text-green-400">
                        ستتلقى اتصالا من طرفنا لتأكيد طلبك قريباً.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Desktop Order Button */}
            {!orderSuccess && (
              <div className="hidden md:block pt-6">
                <button
                  type="button"
                  onClick={() => {
                    if (orderFormRef.current) {
                      orderFormRef.current.submit();
                    }
                  }}
                  disabled={isSubmittingOrder || !isFormValid}
                  className={`w-full font-bold py-4 px-6 rounded-xl shadow-lg transition text-lg ${
                    isSubmittingOrder || !isFormValid
                      ? "opacity-50 cursor-not-allowed bg-slate-400 text-white"
                      : "bg-primary text-white hover:bg-primary/90 shadow-primary/30"
                  }`}
                >
                  {isSubmittingOrder ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="material-symbols-outlined animate-spin">
                        sync
                      </span>
                      جاري المعالجة...
                    </span>
                  ) : !isFormValid ? (
                    "أكمل جميع الحقول المطلوبة"
                  ) : (
                    "تأكيد الطلب"
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Sticky Footer CTA */}
      {!orderSuccess && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-background-light dark:bg-background-dark p-4 border-t border-slate-200 dark:border-slate-800 shadow-lg z-20">
          <button
            type="button"
            onClick={() => {
              if (orderFormRef.current) {
                orderFormRef.current.submit();
              }
            }}
            disabled={isSubmittingOrder || !isFormValid}
            className={`w-full font-bold py-3 px-4 rounded-xl shadow-lg transition ${
              isSubmittingOrder || !isFormValid
                ? "opacity-50 cursor-not-allowed bg-slate-400 text-white"
                : "bg-primary text-white hover:bg-primary/90 shadow-primary/30"
            }`}
          >
            {isSubmittingOrder ? (
              <span className="flex items-center justify-center gap-2">
                <span className="material-symbols-outlined animate-spin">
                  sync
                </span>
                جاري المعالجة...
              </span>
            ) : !isFormValid ? (
              "أكمل جميع الحقول المطلوبة"
            ) : (
              "تأكيد الطلب"
            )}
          </button>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ProductDetail;
